import { createContext, useContext, useMemo, type ReactNode } from "react";
import type { InsightClient } from "./InsightClient";
import { HttpInsightClient } from "./HttpInsightClient";
import { MockInsightClient } from "./MockInsightClient";
import { API_BASE, API_KEY } from "../analytics/config";

/** Gateway insights base. `API_BASE` is the gateway mount root (e.g.
 *  "/coding-gateway/internal" or "https://api.ai21.com/tokenwise"); the insights
 *  read API is a sibling sub-path under it, so we append `/insights` the same
 *  way the settings clients append `/settings/agents`. */
function insightsBase(): string | undefined {
  if (!API_BASE) return undefined;

  return `${API_BASE.replace(/\/+$/, "")}/insights`;
}

const InsightClientContext = createContext<InsightClient | null>(null);

/** Real insights client when the gateway is configured; otherwise the mock. */
export function createDefaultInsightClient(): InsightClient {
  const base = insightsBase();
  return base ? new HttpInsightClient(base, API_KEY) : new MockInsightClient();
}

export function InsightProvider({
  children,
  client,
}: {
  children: ReactNode;
  client?: InsightClient;
}) {
  const value = useMemo(() => client ?? createDefaultInsightClient(), [client]);
  return (
    <InsightClientContext.Provider value={value}>
      {children}
    </InsightClientContext.Provider>
  );
}

export function useInsightClient(): InsightClient {
  const client = useContext(InsightClientContext);
  if (!client) {
    throw new Error("useInsightClient must be used within an InsightProvider");
  }

  return client;
}
